import React, { useEffect, useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  DragOverEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import ItemsTimeline from './ItemsTimeline';
import { useUpdateItemsSortOrder } from '@/hooks/useItemsSortOrder';
import { useItemsReorderPermission } from '@/hooks/useItemsReorderPermission';
import type { TimelineItem, ItemFilters, ActiveFunctions } from '@/types/item';
import type { ItemOrderStatus } from '@/hooks/procurement/useItemOrderStatus';
import { ArrowUpDown } from 'lucide-react';
import ItemRowGhost from './ItemRowGhost';
import SortableItemRow from './SortableItemRow';
import DropIndicator from './DropIndicator';
import DragHandle from './DragHandle';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTimelineRenderObserver } from '@/hooks/useTimelineRenderObserver';

interface DraggableItemsTimelineProps {
  items: TimelineItem[];
  activeFunctions: ActiveFunctions;
  displayMode: 'normalized' | 'absolute';
  anchorDate: string; // ISO date string (YYYY-MM-DD)
  showProjectColumn: boolean;
  showCategoryColumn?: boolean;
  filters: ItemFilters;
  onItemClick: (itemId: string) => void;
  onMilestoneClick?: (itemId: string, milestoneId: string) => void;
  onCreateParallelItem?: (afterItemId: string, afterSortOrder: number, name: string) => Promise<void> | void;
  onFunctionClick: (item: TimelineItem, functionType: string) => void;
  onDeleteItem: (itemId: string) => void;
  projectId?: string;
  reorderModeEnabled?: boolean;
  expandedItems?: Set<string>;
  onToggleExpand?: (itemId: string) => void;
  onExpandAll?: () => void;
  onCollapseAll?: () => void;
  showItemTypeBadges?: boolean; // Show SVC/BLD badges (default: false)
  summaryMilestoneIds?: Set<string>;  // ✅ NEW - IDs of selected items/milestones
  summaryMilestones?: any[];  // ✅ NEW - Resolved summary milestones (with dates) for overlay connector
  summarySelectionMode?: boolean;  // ✅ NEW - Enable milestone selection mode
  onToggleSummarySelection?: (
    id: string,
    type: 'item-start' | 'item-end' | 'milestone' | 'milestone-start' | 'milestone-end',
    data: { itemId: string; itemName: string; date: string; color: string; name?: string }
  ) => void;  // ✅ NEW - Handler for toggle selection
  stableItemIds?: string[]; // Stable sorted item ids for depends_on queries (avoid refire on sort/paginate)
  itemOrderStatus?: Map<string, ItemOrderStatus>;
  onSetItemGroup?: (itemId: string, group: string | null) => void;
  showGroupRows?: boolean;
  groupStorageKey?: string;
  expandAllSignal?: number;
  collapseAllSignal?: number;
}


const DraggableItemsTimeline: React.FC<DraggableItemsTimelineProps> = ({
  items,
  activeFunctions,
  displayMode,
  anchorDate,
  showProjectColumn,
  showCategoryColumn = false,
  filters,
  onItemClick,
  onMilestoneClick,
  onCreateParallelItem,
  onFunctionClick,
  onDeleteItem,
  projectId,
  showItemTypeBadges = false,
  reorderModeEnabled = false,
  expandedItems,
  onToggleExpand,
  onExpandAll,
  onCollapseAll,
  summaryMilestoneIds,
  summaryMilestones,
  summarySelectionMode = false,
  onToggleSummarySelection,
  stableItemIds,
  itemOrderStatus,
  onSetItemGroup,
  showGroupRows,
  groupStorageKey,
  expandAllSignal,
  collapseAllSignal
}) => {
  const [localItems, setLocalItems] = useState(items);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const updateSortOrder = useUpdateItemsSortOrder();
  const { canReorder } = useItemsReorderPermission(projectId);
  const isMobile = useIsMobile();
  
  // Timeline render performance monitoring
  const {
    startRenderTiming,
    endRenderTiming,
    setupIntersectionObserver,
  } = useTimelineRenderObserver({
    enabled: items.length > 50, // Only monitor for larger datasets
    itemCount: items.length,
    onMetricsCollected: (metrics) => {
      logger.debug('[Timeline Performance]', {
        renderTime: metrics.timelineRenderEnd - metrics.timelineRenderStart,
        itemsRendered: metrics.itemsRendered,
        layoutShifts: metrics.layoutShifts
      });
    }
  });
  
  // Only enable drag & drop when both permission and mode are enabled
  const isDragEnabled = canReorder && reorderModeEnabled;
  
  // Enhanced debug logging
  console.log('🎯 [DraggableItemsTimeline] State:', {
    canReorder,
    reorderModeEnabled,
    isDragEnabled,
    projectId,
    itemsCount: items.length
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Update local items when items prop changes
  useEffect(() => {
    startRenderTiming();
    setLocalItems(items);
    // End timing after state update is processed
    const timeoutId = setTimeout(() => {
      endRenderTiming();
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [items, startRenderTiming, endRenderTiming]);

  // Real-time sync for sort order changes
  useEffect(() => {
    if (!projectId) return;

    const channel = supabase
      .channel('items-sort-order-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'items',
          filter: `project_id=eq.${projectId}`,
        },
        () => {
          // Just invalidate queries to refetch latest data
          // This prevents sorting conflicts during real-time updates
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [projectId]);

  const handleDragStart = (event: DragStartEvent) => {
    console.log('🎯 [DragStart] Item:', event.active.id);
    setActiveId(event.active.id as string);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event;
    setOverId(over ? over.id as string : null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    console.log('🎯 [DragEnd] Called with:', {
      activeId: event.active.id,
      overId: event.over?.id,
      projectId,
      hasOver: !!event.over,
      sameId: event.active.id === event.over?.id
    });
    
    const { active, over } = event;
    setActiveId(null);
    setOverId(null);

    if (!over) {
      console.log('❌ [DragEnd] No over target - aborting');
      return;
    }
    
    if (active.id === over.id) {
      console.log('❌ [DragEnd] Same position - aborting');
      return;
    }
    
    if (!projectId) {
      console.log('❌ [DragEnd] No projectId - aborting');
      return;
    }
    
    console.log('✅ [DragEnd] All checks passed, proceeding with reorder');

    const oldIndex = localItems.findIndex(item => item.id === active.id);
    const newIndex = localItems.findIndex(item => item.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const newItems = arrayMove(localItems, oldIndex, newIndex);
      
      // Optimistic update
      setLocalItems(newItems);

      try {
        // Fetch all project items to get global ordering
        const { data: allProjectItems, error } = await supabase
          .from('items')
          .select('id, sort_order')
          .eq('project_id', projectId)
          .order('sort_order', { ascending: true })
          .order('created_at', { ascending: false });

        if (error) throw error;

        // Build global item order array
        const fullItemIds = allProjectItems?.map(item => item.id) || [];
        logger.debug('📊 Full project items:', { itemCount: fullItemIds.length, projectId });
        
        // Remove dragged item and insert at new position
        const draggedItemId = active.id as string;
        const overItemId = over.id as string;
        logger.debug('🎯 Drag operation:', { draggedItemId, overItemId });
        
        // Find positions in global order
        const draggedIndex = fullItemIds.findIndex(id => id === draggedItemId);
        const overIndex = fullItemIds.findIndex(id => id === overItemId);
        logger.debug('📍 Global positions:', { draggedIndex, overIndex });
        
        // Create new order by moving dragged item to correct position
        const finalOrder = [...fullItemIds];
        finalOrder.splice(draggedIndex, 1); // Remove from old position
        
        // Recompute target index after removal and insert BEFORE the over item
        const newOverIndex = finalOrder.findIndex(id => id === overItemId);
        const insertIndex = newOverIndex < 0 ? finalOrder.length : newOverIndex;
        finalOrder.splice(insertIndex, 0, draggedItemId);
        
        // Skip if order didn't actually change
        if (finalOrder.join('|') === fullItemIds.join('|')) {
          logger.debug('ℹ️ No order change detected, skipping update');
          return;
        }
        logger.debug('✅ Final order:', { finalOrderLength: finalOrder.length, projectId });
        
        // Create sort order updates for all affected items
        const sortOrderUpdates = finalOrder.map((id, index) => ({
          id,
          sort_order: index + 1,
        }));

        // Update database with enhanced error handling
        console.log('📤 [DragEnd] Calling mutation with updates:', sortOrderUpdates.length);
        updateSortOrder.mutate(sortOrderUpdates, {
          onError: (error) => {
            console.error('❌ [DragEnd] Mutation failed:', error);
            logger.error('Drag & drop save failed', {
              error: error.message,
              itemsCount: sortOrderUpdates.length,
              projectId
            });
            
            // Revert optimistic update
            setLocalItems(items);
            
            // Show detailed error message
            const isNetworkError = error.message?.includes('fetch') || error.message?.includes('network');
            const errorMsg = isNetworkError 
              ? 'Mất kết nối. Thứ tự đã được khôi phục.' 
              : 'Lỗi lưu dữ liệu. Vui lòng thử lại.';
              
            logger.debug('Drag error details', { isNetworkError, errorMsg });
          },
          onSuccess: () => {
            console.log('✅ [DragEnd] Mutation successful!');
            logger.debug('Drag & drop save successful', {
              itemsCount: sortOrderUpdates.length,
              projectId
            });
          }
        });
      } catch (error) {
        logger.error('Failed to fetch project items for reordering', {
          error: error.message,
          projectId
        });
        
        // Revert optimistic update on fetch error
        setLocalItems(items);
      }
    }
  };

  const activeItem = activeId ? localItems.find(item => item.id === activeId) : null;

  // If drag mode is not enabled, show non-draggable timeline
  if (!isDragEnabled) {
    return (
      <div className="space-y-4">
        {reorderModeEnabled && canReorder && (
          <div className="bg-primary/5 border border-primary/10 rounded-lg p-3">
            <div className="flex items-center gap-2 text-primary">
              <ArrowUpDown className="h-4 w-4" />
              <span className="text-sm font-medium">Chế độ sắp xếp đang bật</span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Kéo thả các items để thay đổi thứ tự
            </p>
          </div>
        )}
        <ItemsTimeline
          items={localItems}
          activeFunctions={activeFunctions}
          displayMode={displayMode}
          anchorDate={anchorDate}
          showProjectColumn={showProjectColumn}
          showCategoryColumn={showCategoryColumn}
          filters={filters}
          onItemClick={onItemClick}
          onMilestoneClick={onMilestoneClick}
          onCreateParallelItem={onCreateParallelItem}
          onFunctionClick={onFunctionClick}
          onDeleteItem={onDeleteItem}
          isReorderMode={isDragEnabled}
          expandedItems={expandedItems}
          onToggleExpand={onToggleExpand}
          onExpandAll={onExpandAll}
          onCollapseAll={onCollapseAll}
          showItemTypeBadges={showItemTypeBadges}
          summaryMilestoneIds={summaryMilestoneIds}
          summaryMilestones={summaryMilestones}
          summarySelectionMode={summarySelectionMode}
          onToggleSummarySelection={onToggleSummarySelection}
          stableItemIds={stableItemIds}
          itemOrderStatus={itemOrderStatus}
          onSetItemGroup={onSetItemGroup}
          showGroupRows={showGroupRows}
          groupStorageKey={groupStorageKey}
          expandAllSignal={expandAllSignal}
          collapseAllSignal={collapseAllSignal}
          enableZoom
        />
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={localItems.map(item => item.id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-4">
          {/* Reorder Mode Banner */}
          <div className="bg-primary/5 border border-primary/10 rounded-lg p-3">
            <div className="flex items-center gap-2 text-primary">
              <ArrowUpDown className="h-4 w-4" />
              <span className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium`}>Đang ở chế độ sắp xếp</span>
            </div>
            <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-muted-foreground mt-1`}>
              {isMobile ? 'Kéo thả để sắp xếp' : 'Kéo thả các items để thay đổi thứ tự. Một đường kẻ xanh sẽ hiển thị vị trí thả.'}
            </p>
          </div>

          <div className="flex">
            {/* Drag handles column */}
            <div className={`w-10 flex flex-col ${isMobile ? 'pt-[50px]' : 'pt-[60px]'}`}>
              {localItems.map((item, index) => (
                <div key={item.id} className={`relative ${isMobile ? 'h-[35px]' : 'h-10'} flex items-center`}>
                  <SortableItemRow
                    item={item}
                  >
                    <DragHandle 
                      className="group"
                      isDragging={activeId === item.id}
                    />
                  </SortableItemRow>
                  
                  {/* Drop indicators */}
                  <DropIndicator 
                    isVisible={overId === item.id && activeId !== item.id}
                    position="above"
                  />
                  {index === localItems.length - 1 && (
                    <DropIndicator 
                      isVisible={overId === item.id && activeId !== item.id}
                      position="below"
                    />
                  )}
                </div>
              ))}
            </div>
            
            {/* Timeline Content */}
            <div 
              className="flex-1"
              ref={(el) => {
                if (el) setupIntersectionObserver(el);
              }}
            >
          <ItemsTimeline
            items={localItems}
            activeFunctions={activeFunctions}
            displayMode={displayMode}
            anchorDate={anchorDate}
            showProjectColumn={showProjectColumn}
            showCategoryColumn={showCategoryColumn}
            filters={filters}
            onItemClick={onItemClick}
            onMilestoneClick={onMilestoneClick}
            onCreateParallelItem={onCreateParallelItem}
            onFunctionClick={onFunctionClick}
            onDeleteItem={onDeleteItem}
            isReorderMode={isDragEnabled}
            expandedItems={expandedItems}
            onToggleExpand={onToggleExpand}
            onExpandAll={onExpandAll}
            onCollapseAll={onCollapseAll}
            showItemTypeBadges={showItemTypeBadges}
            summaryMilestoneIds={summaryMilestoneIds}
            summaryMilestones={summaryMilestones}
            summarySelectionMode={summarySelectionMode}
            onToggleSummarySelection={onToggleSummarySelection}
            stableItemIds={stableItemIds}
            itemOrderStatus={itemOrderStatus}
            onSetItemGroup={onSetItemGroup}
            showGroupRows={showGroupRows}
            groupStorageKey={groupStorageKey}
            expandAllSignal={expandAllSignal}
            collapseAllSignal={collapseAllSignal}
            enableZoom
          />
            </div>
          </div>
        </div>
      </SortableContext>

      <DragOverlay style={{ pointerEvents: 'none' }}>
        {activeId && activeItem && (
          <ItemRowGhost 
            item={activeItem}
            activeFunctions={activeFunctions}
            showProjectColumn={showProjectColumn}
          />
        )}
      </DragOverlay>
    </DndContext>
  );
};

export default DraggableItemsTimeline;